import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, Edit2, Trash2, Calendar, Phone, User, Heart, AlertCircle, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MARITAL_STATUS = ['Célibataire', 'Marié(e)', 'Divorcé(e)', 'Veuf/Veuve'];
const INSURANCE_TYPES = ['Aucune', 'CNAS', 'CASNOS', 'Mutuelle', 'MGEN', 'Privée', 'Autre'];
const REFERRAL_SOURCES = ['Google', 'Tabbibak.com', 'Bouche-à-oreille', 'Recommandation médecin', 'Réseaux sociaux', 'Passage devant clinique', 'Autre'];

const ALLERGIES_LIST = [
  'Pénicilline', 'Aspirine', 'AINS (Ibuprofène)', 'Latex', 'Iode',
  'Anesthésiques locaux', 'Sulfamides', 'Œufs', 'Lait/Lactose', 'Gluten',
  'Fruits à coque', 'Fruits de mer', 'Poussière/Acariens', 'Pollen', 'Animaux',
];

const CHRONIC_DISEASES_LIST = [
  'Diabète type 1', 'Diabète type 2', 'Hypertension artérielle', 'Asthme',
  'Cardiopathie', 'AVC', 'Cancer', 'Hépatite B', 'Hépatite C', 'VIH',
  'Insuffisance rénale', 'Hyperthyroïdie', 'Hypothyroïdie', 'Épilepsie',
  'Dépression', 'Anxiété', 'Arthrose', 'Polyarthrite', 'Ostéoporose',
  'BPCO', 'Maladie de Crohn', 'Reflux gastrique',
];

// Motifs de consultation par specialite
const MOTIFS_BY_SPECIALTY = {
  'Dentiste': ['Carie dentaire', 'Détartrage', 'Extraction', 'Couronne', 'Bridge', 'Implant', 'Orthodontie', 'Blanchiment', 'Douleur dentaire', 'Saignement gencives', 'Prothèse', 'Contrôle de routine', 'Urgence dentaire', 'Dévitalisation', 'Facette'],
  'Orthodontie': ['Appareil dentaire', 'Aligneurs invisibles', 'Bilan orthodontique', 'Réglage', 'Contention'],
  'Médecin généraliste': ['Consultation générale', 'Fièvre', 'Toux', 'Maux de tête', 'Maux de dos', 'Fatigue', 'Renouvellement ordonnance', 'Bilan de santé', 'Certificat médical', 'Vaccination', 'Insomnie', 'Stress'],
  'Cardiologue': ['Bilan cardiaque', 'Douleur thoracique', 'Palpitations', 'Hypertension', 'ECG', 'Échographie cœur', 'Essoufflement', 'Holter tensionnel', 'Suivi post-infarctus'],
  'Ophtalmologue': ['Bilan de la vue', 'Lunettes/Lentilles', 'Cataracte', 'Glaucome', 'Yeux rouges', 'Larmoiement', 'DMLA', 'Strabisme', 'Rétinopathie diabétique'],
  'Gynécologue': ['Bilan annuel', 'Grossesse', 'Contraception', 'Frottis', 'Trouble du cycle', 'Ménopause', 'Infertilité', 'Mammographie', 'IVG', 'Suivi grossesse'],
  'Pédiatre': ['Vaccination', 'Bilan croissance', 'Maladie infantile', 'Allergie', 'Suivi nutrition', 'Fièvre', 'Toux', 'Diarrhée', 'Otite', 'Vaccination obligatoire'],
  'Dermatologue': ['Acné', 'Eczéma', 'Psoriasis', 'Naevus suspect', 'Verrues', 'Chute de cheveux', 'Mycose', 'Allergie cutanée', 'Cicatrice'],
  'ORL': ['Sinusite', 'Otite', 'Surdité', 'Vertige', 'Mal de gorge', 'Polype nasal', 'Ronflement', 'Apnée du sommeil', 'Acouphènes'],
  'Pneumologue': ['Asthme', 'BPCO', 'Tabagisme', 'Allergie respiratoire', 'Apnée du sommeil', 'Tuberculose', 'Pneumonie', 'Toux chronique'],
  'Gastro-entérologue': ['Reflux gastrique', 'Ulcère', 'Coloscopie', 'Endoscopie', 'Maladie de Crohn', 'Hépatite', 'Problème de foie', 'Constipation', 'Diarrhée chronique'],
  'Endocrinologue': ['Diabète', 'Thyroïde', 'Obésité', 'Ménopause', 'Trouble hormonal', 'Croissance', 'Hyperthyroïdie', 'Hypothyroïdie'],
  'Neurologue': ['Migraine', 'Épilepsie', 'AVC', 'Sclérose en plaques', 'Alzheimer', 'Parkinson', 'Vertige', 'Tremblement'],
  'Psychiatre': ['Dépression', 'Anxiété', 'Bipolarité', 'Trouble du sommeil', 'Addiction', 'Stress', 'Phobie'],
  'Psychologue': ['Thérapie individuelle', 'Stress', 'Thérapie de couple', 'Phobie', 'Trouble alimentaire', 'Deuil', 'Burn-out'],
  'Rhumatologue': ['Arthrose', 'Polyarthrite', 'Tendinite', 'Ostéoporose', 'Lombalgie', 'Fibromyalgie', 'Rhumatisme'],
  'Urologue': ['Prostate', 'Calcul rénal', 'Infection urinaire', 'Incontinence', 'Vasectomie', 'Trouble érectile'],
  'Chirurgien orthopédique': ['Fracture', 'Entorse', 'Arthrose', 'Prothèse hanche/genou', 'Hernie discale', 'Tendinite', 'Méniscectomie'],
  'Allergologue': ['Bilan allergique', 'Désensibilisation', 'Asthme allergique', 'Eczéma', 'Allergies alimentaires', 'Rhinite allergique'],
  'Nutritionniste': ['Perte de poids', 'Diabète', 'Obésité', 'Régime sportif', 'Trouble alimentaire', 'Cholestérol'],
  'Radiologue': ['Échographie', 'Scanner', 'IRM', 'Mammographie', 'Radiographie', 'Doppler'],
};

const getAuthConfig = () => {
  // Correctif B7 : le cookie httpOnly porte la session, plus d'en-tete Authorization.
  return {
    withCredentials: true,
    headers: {},
  };
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

// Multi-select avec checkboxes + autre
const MultiCheckSelect = ({ options, value, onChange, otherValue, onOtherChange }) => {
  const selected = (value || '').split(',').map(s => s.trim()).filter(Boolean);

  const toggle = (item) => {
    let next;
    if (selected.includes(item)) {
      next = selected.filter(s => s !== item);
    } else {
      next = [...selected, item];
    }
    onChange(next.join(', '));
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 p-3 bg-[#F8FAFC] rounded-lg max-h-48 overflow-y-auto">
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white p-1 rounded">
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="w-3.5 h-3.5" />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      <Input value={otherValue || ''} onChange={(e) => onOtherChange(e.target.value)} placeholder="Autre (préciser)..." className="text-sm" />
    </div>
  );
};

const Section = ({ title, icon: Icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-4 py-3 bg-[#F8FAFC] flex items-center justify-between hover:bg-[#F1F5F9] transition">
        <div className="flex items-center gap-2 font-medium text-[#0F172A]">
          {Icon && <Icon className="w-4 h-4 text-[#0596DE]" />}
          {title}
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="p-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
};

const PatientsTab = () => {
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDoctor, setFilterDoctor] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [viewingPatient, setViewingPatient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showRdvForm, setShowRdvForm] = useState(false);
  const [rdvData, setRdvData] = useState({ doctor_id: '', date: '', time: '', reason: '' });
  const [rdvSaving, setRdvSaving] = useState(false);

  const emptyForm = {
    first_name: '', last_name: '', phone: '', gender: '', date_of_birth: '',
    birth_place: '', nationality: 'Algérienne',
    phone_secondary: '', email: '', address: '', commune: '', zip_code: '',
    marital_status: '', profession: '', employer: '',
    emergency_contact_name: '', emergency_contact_relation: '', emergency_contact_phone: '',
    insurance_type: '', insurance_number: '', insurance_company: '',
    blood_type: '', height_cm: '', weight_kg: '',
    allergies: '', allergies_other: '',
    chronic_diseases: '', chronic_diseases_other: '',
    current_medications: '', surgical_history: '', family_history: '',
    smoking: '', alcohol: '',
    is_pregnant: false, number_of_children: '',
    first_visit_reason: '', first_visit_reason_other: '',
    assigned_doctor_id: '', referral_source: '',
    medical_notes: '', notes: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadPatients(); }, [search, filterDoctor]);
  useEffect(() => {
    axios.get(`${API_URL}/api/clinic/doctors`, getAuthConfig())
      .then(r => setDoctors(r.data || []))
      .catch(() => {});
  }, []);

  const loadPatients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterDoctor && filterDoctor !== 'all') params.append('doctor_id', filterDoctor);
      const r = await axios.get(`${API_URL}/api/clinic/patients?${params}`, getAuthConfig());
      setPatients(r.data.patients || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Erreur chargement patients');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPatient(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingPatient(p);
    setFormData({ ...emptyForm, ...p });
    setShowForm(true);
    setViewingPatient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.phone.trim()) {
      toast.error('Prénom, nom et téléphone obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      // Fusionner les valeurs "autre" dans les champs principaux
      if (payload.allergies_other) {
        payload.allergies = (payload.allergies + (payload.allergies ? ', ' : '') + payload.allergies_other).trim();
      }
      delete payload.allergies_other;
      if (payload.chronic_diseases_other) {
        payload.chronic_diseases = (payload.chronic_diseases + (payload.chronic_diseases ? ', ' : '') + payload.chronic_diseases_other).trim();
      }
      delete payload.chronic_diseases_other;
      if (payload.first_visit_reason_other) {
        payload.first_visit_reason = (payload.first_visit_reason + (payload.first_visit_reason ? ' - ' : '') + payload.first_visit_reason_other).trim();
      }
      delete payload.first_visit_reason_other;

      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      if (payload.height_cm) payload.height_cm = parseInt(payload.height_cm);
      if (payload.weight_kg) payload.weight_kg = parseFloat(payload.weight_kg);
      if (payload.number_of_children) payload.number_of_children = parseInt(payload.number_of_children);

      if (editingPatient) {
        await axios.put(`${API_URL}/api/clinic/patients/${editingPatient.patient_id}`, payload, getAuthConfig());
        toast.success('Patient modifié');
      } else {
        await axios.post(`${API_URL}/api/clinic/patients`, payload, getAuthConfig());
        toast.success('Patient ajouté');
      }
      setShowForm(false);
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer définitivement ${p.first_name} ${p.last_name} ?\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API_URL}/api/clinic/patients/${p.patient_id}`, getAuthConfig());
      toast.success('Patient supprimé');
      setViewingPatient(null);
      loadPatients();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const handleView = async (p) => {
    try {
      const r = await axios.get(`${API_URL}/api/clinic/patients/${p.patient_id}`, getAuthConfig());
      setViewingPatient(r.data);
    } catch (err) {
      toast.error('Erreur chargement');
    }
  };

  const openRdvForm = () => {
    setRdvData({
      doctor_id: viewingPatient?.assigned_doctor_id || '',
      date: '',
      time: '',
      reason: viewingPatient?.first_visit_reason || '',
    });
    setShowRdvForm(true);
  };

  const handleCreateRdv = async (e) => {
    e.preventDefault();
    if (!rdvData.doctor_id || !rdvData.date || !rdvData.time) {
      toast.error('Médecin, date et heure obligatoires');
      return;
    }
    setRdvSaving(true);
    try {
      await axios.post(`${API_URL}/api/clinic/appointments`, {
        ...rdvData,
        clinic_patient_id: viewingPatient.patient_id,
      }, getAuthConfig());
      toast.success('Rendez-vous programmé');
      setShowRdvForm(false);
      // Recharger la fiche du patient pour mettre a jour l'historique
      handleView(viewingPatient);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création RDV');
    } finally {
      setRdvSaving(false);
    }
  };

  const getDoctorName = (id) => doctors.find(d => d.doctor_id === id)?.name || '—';
  const getDoctorSpecialty = (id) => doctors.find(d => d.doctor_id === id)?.specialty || '';
  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Motifs de consultation en fonction du medecin choisi
  const selectedDoctorSpecialty = getDoctorSpecialty(formData.assigned_doctor_id);
  const availableMotifs = MOTIFS_BY_SPECIALTY[selectedDoctorSpecialty] || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input placeholder="Rechercher par nom, prénom, téléphone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-full" />
        </div>
        <Select value={filterDoctor} onValueChange={setFilterDoctor}>
          <SelectTrigger className="w-full md:w-56 rounded-full"><SelectValue placeholder="Tous les médecins" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les médecins</SelectItem>
            {doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openCreate} className="rounded-full bg-[#0596DE] hover:bg-[#047CBA] gap-2"><Plus className="w-4 h-4" /> Ajouter patient</Button>
      </div>

      <div className="bg-[#F8FAFC] p-4 rounded-xl text-sm text-[#475569]">
        <strong className="text-[#0F172A]">{total}</strong> patient{total > 1 ? 's' : ''} {search ? `correspondant à "${search}"` : 'dans la clinique'}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B]">Chargement...</div>
      ) : patients.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-[#64748B]">{search ? 'Aucun résultat' : 'Aucun patient enregistré'}</p>
          {!search && <Button onClick={openCreate} className="rounded-full mt-4 gap-2"><Plus className="w-4 h-4" /> Ajouter le premier patient</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {patients.map(p => (
            <div key={p.patient_id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-md transition cursor-pointer" onClick={() => handleView(p)}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-[#0F172A] truncate">{p.first_name} {p.last_name}</h4>
                  <p className="text-xs text-[#64748B] mt-1">
                    {p.gender === 'M' ? '♂ H' : p.gender === 'F' ? '♀ F' : ''}
                    {p.date_of_birth && ` • ${calculateAge(p.date_of_birth)} ans`}
                  </p>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(p)}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              {p.phone && <div className="flex items-center gap-1.5 text-xs text-[#64748B] mt-1"><Phone className="w-3 h-3" /> {p.phone}</div>}
              {p.assigned_doctor_id && <div className="text-xs text-[#0596DE] mt-2 truncate">{formatDoctorName(getDoctorName(p.assigned_doctor_id))}</div>}
            </div>
          ))}
        </div>
      )}

      {/* FORMULAIRE */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle>{editingPatient ? 'Modifier le patient' : 'Nouveau patient'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">

            <Section title="1. Identité & Coordonnées" icon={User} defaultOpen={true}>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom *</Label><Input value={formData.first_name} onChange={(e) => update('first_name', e.target.value)} required /></div>
                <div><Label>Nom *</Label><Input value={formData.last_name} onChange={(e) => update('last_name', e.target.value)} required /></div>
                <div><Label>Téléphone *</Label><Input type="tel" value={formData.phone} onChange={(e) => update('phone', e.target.value)} required placeholder="+213..." /></div>
                <div><Label>Téléphone secondaire</Label><Input type="tel" value={formData.phone_secondary} onChange={(e) => update('phone_secondary', e.target.value)} /></div>
                <div>
                  <Label>Sexe</Label>
                  <Select value={formData.gender} onValueChange={(v) => update('gender', v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Homme</SelectItem>
                      <SelectItem value="F">Femme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Date de naissance</Label><Input type="date" value={formData.date_of_birth || ''} onChange={(e) => update('date_of_birth', e.target.value)} /></div>
                <div>
                  <Label>État civil</Label>
                  <Select value={formData.marital_status} onValueChange={(v) => update('marital_status', v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{MARITAL_STATUS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => update('email', e.target.value)} /></div>
                <div className="col-span-2"><Label>Adresse</Label><Input value={formData.address} onChange={(e) => update('address', e.target.value)} /></div>
                <div><Label>Commune</Label><Input value={formData.commune} onChange={(e) => update('commune', e.target.value)} /></div>
                <div><Label>Code postal</Label><Input value={formData.zip_code} onChange={(e) => update('zip_code', e.target.value)} /></div>

              </div>
            </Section>

            <Section title="2. Contact d'urgence" icon={AlertCircle}>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom complet</Label><Input value={formData.emergency_contact_name} onChange={(e) => update('emergency_contact_name', e.target.value)} /></div>
                <div><Label>Lien de parenté</Label><Input value={formData.emergency_contact_relation} onChange={(e) => update('emergency_contact_relation', e.target.value)} placeholder="ex: Conjoint(e), Père, Mère..." /></div>
                <div className="col-span-2"><Label>Téléphone</Label><Input type="tel" value={formData.emergency_contact_phone} onChange={(e) => update('emergency_contact_phone', e.target.value)} /></div>
              </div>
            </Section>

            <Section title="3. Assurance / Mutuelle" icon={Shield}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={formData.insurance_type} onValueChange={(v) => update('insurance_type', v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{INSURANCE_TYPES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>N° d'assuré</Label><Input value={formData.insurance_number} onChange={(e) => update('insurance_number', e.target.value)} /></div>
                <div className="col-span-2"><Label>Société / Mutuelle</Label><Input value={formData.insurance_company} onChange={(e) => update('insurance_company', e.target.value)} /></div>
              </div>
            </Section>

            <Section title="4. Données médicales" icon={Heart}>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Groupe sanguin</Label>
                  <Select value={formData.blood_type} onValueChange={(v) => update('blood_type', v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{BLOOD_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Taille (cm)</Label><Input type="number" value={formData.height_cm} onChange={(e) => update('height_cm', e.target.value)} /></div>
                <div><Label>Poids (kg)</Label><Input type="number" step="0.1" value={formData.weight_kg} onChange={(e) => update('weight_kg', e.target.value)} /></div>
              </div>

              <div>
                <Label>Allergies (cocher tout ce qui s'applique)</Label>
                <MultiCheckSelect options={ALLERGIES_LIST} value={formData.allergies} onChange={(v) => update('allergies', v)} otherValue={formData.allergies_other} onOtherChange={(v) => update('allergies_other', v)} />
              </div>

              <div>
                <Label>Maladies chroniques (cocher tout ce qui s'applique)</Label>
                <MultiCheckSelect options={CHRONIC_DISEASES_LIST} value={formData.chronic_diseases} onChange={(v) => update('chronic_diseases', v)} otherValue={formData.chronic_diseases_other} onOtherChange={(v) => update('chronic_diseases_other', v)} />
              </div>

              <div><Label className="text-red-600">⚠️ Médicaments en cours (important: anticoagulants !)</Label><Textarea value={formData.current_medications} onChange={(e) => update('current_medications', e.target.value)} rows={2} /></div>
              <div><Label>Antécédents chirurgicaux</Label><Textarea value={formData.surgical_history} onChange={(e) => update('surgical_history', e.target.value)} rows={2} /></div>
              <div><Label>Antécédents familiaux</Label><Textarea value={formData.family_history} onChange={(e) => update('family_history', e.target.value)} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tabac</Label><Input value={formData.smoking} onChange={(e) => update('smoking', e.target.value)} placeholder="Non / 10 cig/j" /></div>
                <div><Label>Alcool</Label><Input value={formData.alcohol} onChange={(e) => update('alcohol', e.target.value)} placeholder="Non / Occasionnel" /></div>
              </div>
              {formData.gender === 'F' && (
                <div className="grid grid-cols-2 gap-3 bg-[#FDF2F8] p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="pregnant" checked={formData.is_pregnant} onChange={(e) => update('is_pregnant', e.target.checked)} />
                    <Label htmlFor="pregnant">Grossesse en cours</Label>
                  </div>
                  <div><Label>Nombre d'enfants</Label><Input type="number" value={formData.number_of_children} onChange={(e) => update('number_of_children', e.target.value)} /></div>
                </div>
              )}
            </Section>

            <Section title="5. Premier contact" icon={Calendar}>
              <div>
                <Label>Médecin référent</Label>
                <Select value={formData.assigned_doctor_id} onValueChange={(v) => update('assigned_doctor_id', v)}>
                  <SelectTrigger><SelectValue placeholder="Choisir le médecin..." /></SelectTrigger>
                  <SelectContent>
                    {doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name} — {d.specialty}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {availableMotifs.length > 0 ? (
                <>
                  <div>
                    <Label>Motif de consultation ({selectedDoctorSpecialty})</Label>
                    <Select value={formData.first_visit_reason} onValueChange={(v) => update('first_visit_reason', v)}>
                      <SelectTrigger><SelectValue placeholder="Choisir un motif..." /></SelectTrigger>
                      <SelectContent>
                        {availableMotifs.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Autre motif (préciser si pas dans la liste)</Label>
                    <Input value={formData.first_visit_reason_other} onChange={(e) => update('first_visit_reason_other', e.target.value)} placeholder="Description du motif..." />
                  </div>
                </>
              ) : (
                <div>
                  <Label>Motif de consultation</Label>
                  <Textarea value={formData.first_visit_reason} onChange={(e) => update('first_visit_reason', e.target.value)} rows={2} placeholder="Choisissez d'abord un médecin pour voir les motifs..." />
                </div>
              )}


            </Section>

            <Section title="6. Notes personnalisées">
              <div><Label>Notes médicales (spécifiques à votre spécialité)</Label><Textarea value={formData.medical_notes} onChange={(e) => update('medical_notes', e.target.value)} rows={4} placeholder="Antécédents dentaires, ophtalmologiques..." /></div>
              <div><Label>Notes diverses</Label><Textarea value={formData.notes} onChange={(e) => update('notes', e.target.value)} rows={2} /></div>
            </Section>

            <DialogFooter className="sticky bottom-0 bg-white py-3 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#0596DE] hover:bg-[#047CBA]">{saving ? 'Enregistrement...' : (editingPatient ? 'Enregistrer' : 'Ajouter')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingPatient} onOpenChange={() => setViewingPatient(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          {viewingPatient && (
            <>
              <DialogHeader><DialogTitle className="text-2xl">{viewingPatient.first_name} {viewingPatient.last_name}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4 text-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {viewingPatient.gender && <div className="bg-[#F8FAFC] p-2 rounded"><p className="text-xs text-[#64748B]">Sexe</p><p className="font-medium">{viewingPatient.gender === 'M' ? 'Homme' : 'Femme'}</p></div>}
                  {viewingPatient.date_of_birth && <div className="bg-[#F8FAFC] p-2 rounded"><p className="text-xs text-[#64748B]">Âge</p><p className="font-medium">{calculateAge(viewingPatient.date_of_birth)} ans</p></div>}
                  {viewingPatient.blood_type && <div className="bg-[#FEF2F2] p-2 rounded"><p className="text-xs text-[#64748B]">Sang</p><p className="font-bold text-red-600">{viewingPatient.blood_type}</p></div>}
                  {viewingPatient.phone && <div className="bg-[#F8FAFC] p-2 rounded"><p className="text-xs text-[#64748B]">Tél</p><p className="font-medium">{viewingPatient.phone}</p></div>}
                </div>
                {viewingPatient.allergies && <div className="bg-red-50 border border-red-200 p-3 rounded"><strong className="text-red-700">⚠️ Allergies:</strong> {viewingPatient.allergies}</div>}
                {viewingPatient.current_medications && <div className="bg-amber-50 border border-amber-200 p-3 rounded"><strong className="text-amber-700">💊 Médicaments:</strong> {viewingPatient.current_medications}</div>}
                {viewingPatient.chronic_diseases && <div className="bg-[#F8FAFC] p-3 rounded"><strong>Maladies chroniques:</strong> {viewingPatient.chronic_diseases}</div>}
                {viewingPatient.assigned_doctor_id && <div className="bg-[#EFF6FF] p-3 rounded"><strong>Médecin référent:</strong> {formatDoctorName(getDoctorName(viewingPatient.assigned_doctor_id))}</div>}
                {viewingPatient.first_visit_reason && <div className="bg-[#F8FAFC] p-3 rounded"><strong>Motif:</strong> {viewingPatient.first_visit_reason}</div>}
                {viewingPatient.medical_notes && <div className="bg-[#FEFCE8] p-3 rounded whitespace-pre-wrap"><strong>Notes médicales:</strong> {viewingPatient.medical_notes}</div>}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2"><Calendar className="w-4 h-4" /> Historique RDV ({viewingPatient.appointments_count || 0})</h4>
                  {viewingPatient.appointments?.length > 0 ? (
                    <div className="space-y-1">
                      {viewingPatient.appointments.map((a, i) => (
                        <div key={i} className="bg-[#F8FAFC] p-2 rounded text-xs">{new Date(a.date).toLocaleDateString('fr-FR')} {a.time && `à ${a.time}`} — {a.status}</div>
                      ))}
                    </div>
                  ) : <p className="text-[#94A3B8]">Aucun RDV</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleDelete(viewingPatient)} className="text-red-500 border-red-200"><Trash2 className="w-4 h-4 mr-1" /> Supprimer</Button>
                <Button variant="outline" onClick={() => openEdit(viewingPatient)}><Edit2 className="w-4 h-4 mr-1" /> Modifier</Button>
                <Button onClick={openRdvForm} className="bg-[#0596DE] hover:bg-[#047CBA]"><Calendar className="w-4 h-4 mr-1" /> Programmer RDV</Button>
                <Button variant="outline" onClick={() => setViewingPatient(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog programmer RDV */}
      <Dialog open={showRdvForm} onOpenChange={setShowRdvForm}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Programmer un RDV</DialogTitle>
          </DialogHeader>
          {viewingPatient && (
            <form onSubmit={handleCreateRdv} className="space-y-3">
              <div className="bg-[#F8FAFC] p-3 rounded text-sm">
                <strong>Patient:</strong> {viewingPatient.first_name} {viewingPatient.last_name}
              </div>
              <div>
                <Label>Médecin *</Label>
                <Select value={rdvData.doctor_id} onValueChange={(v) => setRdvData({...rdvData, doctor_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir le médecin..." /></SelectTrigger>
                  <SelectContent>
                    {doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name} — {d.specialty}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={rdvData.date} onChange={(e) => setRdvData({...rdvData, date: e.target.value})} required />
                </div>
                <div>
                  <Label>Heure *</Label>
                  <Input type="time" value={rdvData.time} onChange={(e) => setRdvData({...rdvData, time: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label>Motif</Label>
                <Textarea value={rdvData.reason} onChange={(e) => setRdvData({...rdvData, reason: e.target.value})} rows={2} placeholder="Motif de la consultation..." />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRdvForm(false)}>Annuler</Button>
                <Button type="submit" disabled={rdvSaving} className="bg-[#0596DE] hover:bg-[#047CBA]">
                  {rdvSaving ? 'Création...' : 'Confirmer le RDV'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientsTab;
